import { useState } from 'react';
import { Controller, useForm, type UseFormReturn } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { zodResolver } from '@hookform/resolvers/zod';
import { t } from 'i18next';
import { z } from 'zod';

import { ArrowRightIcon } from '../../../assets/ArrowRight';
import { CustomOTP } from '../../../components/form/CustomOTP';
import PhoneField from '../../../components/form/PhoneInput';
import { custonSooner } from '../../../components/CustopmSooner';
import { Form } from '../../../components/ui/form';
import { LoadingButton } from '../../../components/ui/loadingButton';
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
} from '../../../components/ui/drawer';
import {
  DEFAULT_RESEND_AFTER,
  formatCountdown,
  useCountdown,
} from '../../auth/libs/useCountdown';
import { userInfo } from '../../../store/selectors/user';
import { formatPhoneNumberWithCode } from '../../../utils/formatPhone';
import {
  useSendPhoneCode,
  useVerifyCurrentPhoneCode,
  useVerifyPhoneCode,
} from '../services/changePhone';

const PHONE_LENGTH = 9;
const CODE_LENGTH = 4;

/**
 * Сколько раз можно ошибиться в коде, прежде чем придётся запрашивать новый.
 *
 * Считаем на клиенте: `phone/verify-code` отвечает только `success: false`,
 * ни счётчика попыток, ни `retry_after` у него нет. Число то же, что в
 * авторизации, — чтобы поведение не разъезжалось между экранами.
 */
const MAX_CODE_ATTEMPTS = 5;

const BUTTON_CLASS =
  'w-full bg-button-primary h-12 text-[16px] font-semibold font-inter leading-5 tracking-[0px] text-text-white rounded-xl disabled:opacity-60';

// Сообщения — ключи i18n: FormMessage прогоняет error.message через t().
const phoneSchema = z.object({
  phone: z.string().length(PHONE_LENGTH, 'validation.phone'),
});
type PhoneFormSchema = z.infer<typeof phoneSchema>;

const codeSchema = z.object({
  code: z.string().length(CODE_LENGTH, 'validation.code'),
});
type CodeFormSchema = z.infer<typeof codeSchema>;

// Оба кода (на старый и на новый номер) вводятся одинаково — держим один
// экран, чтобы вёрстка OTP не разъезжалась между шагами.
type OtpStepProps = {
  form: UseFormReturn<CodeFormSchema>;
  phone: string;
  description: string;
  loading: boolean;
  /** Секунды до следующего кода; 0 — можно запрашивать. */
  resendSecondsLeft: number;
  isResending: boolean;
  /** Попытки кончились: ввод заперт до нового кода. */
  isLocked: boolean;
  onResend: () => void;
  onBack: () => void;
  onSubmit: (data: CodeFormSchema) => void;
};

const OtpStep = ({
  form,
  phone,
  description,
  loading,
  resendSecondsLeft,
  isResending,
  isLocked,
  onResend,
  onBack,
  onSubmit,
}: OtpStepProps) => (
  <DrawerContent
    handleClassName="mt-0 mb-4"
    className="bg-fill-background px-4 flex flex-col gap-y-2"
  >
    <div className="flex flex-row justify-between items-center">
      <div
        className="flex flex-row items-center space-x-[9px]"
        onClick={onBack}
      >
        <ArrowRightIcon className="rotate-180 h-4 w-2" />
        <DrawerTitle className="text-display-md">{t('back')}</DrawerTitle>
      </div>
    </div>
    <div className="flex flex-col gap-y-4 pt-5 items-center">
      <div className="flex flex-col space-y-2 items-center">
        <p className="text-display-lg">{t('changePhone.otp-title')}</p>
        <div>
          <p className="text-body-sm-regular text-text-secondary text-center">
            {description}
          </p>
          <p className="text-body-sm-regular text-text-secondary text-center">
            {formatPhoneNumberWithCode(phone)}
          </p>
        </div>
      </div>
      <Form {...form}>
        <form className="flex flex-col space-y-8 w-full pb-6">
          <Controller
            control={form.control}
            name="code"
            render={({
              field,
              fieldState: { error },
              formState: { isSubmitted },
            }) => (
              <CustomOTP
                value={field.value || ''}
                onChange={(value) => {
                  field.onChange(value);
                  if (error) form.clearErrors('code');
                }}
                length={CODE_LENGTH}
                error={isSubmitted && !!error}
              />
            )}
          />
          {form.formState.errors.code && (
            <p className="-mt-6 text-caption-sm-medium text-fill-danger text-center">
              {t(String(form.formState.errors.code.message))}
            </p>
          )}
          {/* Повторный код: пока идёт отсчёт — подпись с таймером, после —
              кнопка. Те же правила, что и в авторизации. */}
          {isLocked ? (
            <p className="-mt-6 text-body-sm-regular text-fill-danger text-center">
              {t('auth.otp.tooManyAttempts', {
                time: formatCountdown(resendSecondsLeft),
              })}
            </p>
          ) : resendSecondsLeft === 0 ? (
            <button
              type="button"
              onClick={onResend}
              disabled={isResending}
              className="-mt-6 text-body-sm-medium text-brand-green text-center disabled:opacity-60"
            >
              {t('auth.otp.resend')}
            </button>
          ) : (
            <p className="-mt-6 text-body-sm-regular text-text-tertiary text-center">
              {t('auth.otp.resendIn', {
                time: formatCountdown(resendSecondsLeft),
              })}
            </p>
          )}
          <LoadingButton
            disabled={isLocked}
            loading={loading}
            onClick={form.handleSubmit(onSubmit)}
            className={BUTTON_CLASS}
          >
            {t('continueButton')}
          </LoadingButton>
        </form>
      </Form>
    </div>
  </DrawerContent>
);

type IProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

type Step = 'new' | 'current-code' | 'new-code';

/**
 * Смена номера в профиле в три шага: новый номер -> код на текущий номер ->
 * код на новый номер.
 *
 * Ходит в phone/send-code и phone/verify-code — это отдельная от логина пара
 * эндпоинтов: пользователь уже авторизован, токен не меняется, меняется только
 * номер на аккаунте. Новый номер уходит сразу в send-code (бэк заводит по нему
 * заявку), а код при этом приходит на текущий номер — так подтверждается, что
 * номер меняет владелец аккаунта. Код на новый номер бэк отправляет сам, как
 * только принят код с текущего, поэтому второй send-code не нужен.
 */
export const ChangePhoneDrawer = ({ open, setOpen }: IProps) => {
  const user = useSelector(userInfo);
  const [step, setStep] = useState<Step>('new');

  // Текущий номер в сторе лежит с кодом страны, а API/вёрстка ждут 9 цифр.
  const currentPhone = (user.phone || '')
    .replace(/\D/g, '')
    .slice(-PHONE_LENGTH);

  const { sendPhoneCode, isPending: isSending } = useSendPhoneCode();
  const { verifyCurrentPhoneCode, isPending: isVerifyingCurrent } =
    useVerifyCurrentPhoneCode();
  const { verifyPhoneCode, isPending: isVerifying } = useVerifyPhoneCode();

  const phoneForm = useForm<PhoneFormSchema>({
    resolver: zodResolver(phoneSchema),
    defaultValues: { phone: '' },
  });
  const currentCodeForm = useForm<CodeFormSchema>({
    resolver: zodResolver(codeSchema),
    defaultValues: { code: '' },
  });
  const codeForm = useForm<CodeFormSchema>({
    resolver: zodResolver(codeSchema),
    defaultValues: { code: '' },
  });

  const phone = phoneForm.watch('phone') || '';

  // Отсчёт до следующего кода и запас попыток — общие на оба шага: код в
  // каждый момент подтверждается ровно один.
  const resend = useCountdown();
  const [attemptsLeft, setAttemptsLeft] = useState(MAX_CODE_ATTEMPTS);
  const isLocked = attemptsLeft <= 0;

  /** Код отправлен (первый раз или повторно) — отсчёт и попытки заново. */
  const startCodeWindow = () => {
    resend.start(DEFAULT_RESEND_AFTER);
    setAttemptsLeft(MAX_CODE_ATTEMPTS);
  };

  const reset = () => {
    setStep('new');
    phoneForm.reset();
    currentCodeForm.reset();
    codeForm.reset();
    resend.reset();
    setAttemptsLeft(MAX_CODE_ATTEMPTS);
  };

  /**
   * Повторная отправка. Тот же `send-code`: при активной заявке бэкенд не
   * заводит новую, а переотправляет код текущего шага.
   */
  const handleResend = async () => {
    if (!resend.isOver || isSending || !phone) return;

    const res = await sendPhoneCode(phone);
    if (!res.data.success) return;

    currentCodeForm.reset();
    codeForm.reset();
    startCodeWindow();
  };

  /** Код не подошёл: тратим попытку и чистим поле. */
  const registerWrongCode = (form: UseFormReturn<CodeFormSchema>) => {
    form.reset();
    setAttemptsLeft(left => Math.max(left - 1, 0));
  };

  const close = () => {
    setOpen(false);
    reset();
  };

  const onSubmitPhone = async (data: PhoneFormSchema) => {
    // Текущий номер бэк примет и пришлёт код, но смены не произойдёт —
    // отсекаем здесь, чтобы не гонять пользователя через СМС впустую.
    if (currentPhone === data.phone) {
      custonSooner.error(t('changePhone.same-phone'));
      return;
    }

    const res = await sendPhoneCode(data.phone);

    if (!res.data.success) return;

    currentCodeForm.reset();
    codeForm.reset();
    startCodeWindow();
    // Заявка могла остаться с прошлого захода уже на шаге нового номера —
    // тогда бэк переотправит код на него, и текущий подтверждать не надо.
    setStep(res.data.stage === 'new' ? 'new-code' : 'current-code');
  };

  const onSubmitCurrentCode = async (data: CodeFormSchema) => {
    const res = await verifyCurrentPhoneCode(data.code);

    if (!res.data.success) {
      registerWrongCode(currentCodeForm);
      return;
    }

    // Код на новый номер бэк отправил сам — отсчёт и попытки начинаются
    // заново уже для него.
    startCodeWindow();
    setStep('new-code');
  };

  const onSubmitCode = async (data: CodeFormSchema) => {
    const res = await verifyPhoneCode(data.code);

    if (!res.data.success) {
      registerWrongCode(codeForm);
      return;
    }

    close();
  };

  return (
    <Drawer
      shouldScaleBackground={false}
      open={open}
      onClose={close}
      onOpenChange={close}
      scrollLockTimeout={0}
    >
      {step === 'new' && (
        <DrawerContent
          handleClassName="mt-0 mb-4"
          className="bg-fill-background p-4 flex flex-col gap-y-2 items-center"
        >
          <div className="flex flex-col gap-y-4">
            <div className="flex flex-col space-y-2 items-center">
              <DrawerTitle className="text-display-lg">
                {t('changePhone.title')}
              </DrawerTitle>
              <p className="text-body-sm-regular text-text-secondary max-w-[240px] text-center">
                {t('changePhone.description')}
              </p>
              <Form {...phoneForm}>
                <form className="flex flex-col space-y-5 pb-8 pt-4 w-full h-full">
                  <PhoneField
                    name="phone"
                    inputMode="numeric"
                    className="w-full h-16 border-none text-[22px] font-semibold rounded-[16px] max-w-[343px] leading-7 tracking-[0px] caret-button-primary px-4 py-[18px]"
                  />
                  <LoadingButton
                    disabled={phone.length !== PHONE_LENGTH}
                    loading={isSending}
                    onClick={phoneForm.handleSubmit(onSubmitPhone)}
                    className={BUTTON_CLASS}
                  >
                    {t('continueButton')}
                  </LoadingButton>
                </form>
              </Form>
            </div>
          </div>
        </DrawerContent>
      )}

      {step === 'current-code' && (
        <OtpStep
          form={currentCodeForm}
          phone={currentPhone}
          description={t('changePhone.current-description')}
          loading={isVerifyingCurrent}
          resendSecondsLeft={resend.secondsLeft}
          isResending={isSending}
          isLocked={isLocked}
          onResend={handleResend}
          onBack={() => setStep('new')}
          onSubmit={onSubmitCurrentCode}
        />
      )}

      {step === 'new-code' && (
        <OtpStep
          form={codeForm}
          phone={phone}
          description={t('changePhone.otp-description')}
          loading={isVerifying}
          resendSecondsLeft={resend.secondsLeft}
          isResending={isSending}
          isLocked={isLocked}
          onResend={handleResend}
          // На ввод номера, а не на предыдущий код: тот уже подтверждён и
          // одноразовый. Если пользователь вернулся, значит хочет поправить
          // номер — заявка при повторной отправке переоткроется сама, и
          // `stage` вернёт на нужный шаг.
          onBack={() => setStep('new')}
          onSubmit={onSubmitCode}
        />
      )}
    </Drawer>
  );
};
